import { html } from 'lit-element';
import {
  KatalComponent,
  KatLitElement,
  booleanConverter,
  coerceBooleanClassProperties,
  dateToStringConverter,
} from '../../shared/base';
import { ifNotNull } from '../../utils/directives';

/**
 * @component {kat-date-range-picker} KatalDaterangepicker Allows a user to select a range of dates and provides a display for the selected dates.
 * @guideline Do Best used when you want to collect a range of dates
 * @example UsLocale {"locale": "en-US"}
 * @example GermanLocale {"locale": "de-DE"}
 * @example DisabledWeekends {"id": "disabled-weekends", "locale": "en-US", "start-label": "Disabled Weekends",
 * "script": "
 *   var rangePicker = document.querySelector(\"kat-date-range-picker\");
 *   var disableWeekends = function (date) {
 *     return (date.getDay() === 0 || date.getDay() === 6);
 *   };
 *   rangePicker.startPicker.isDateDisabled = disableWeekends;
 *   rangePicker.endPicker.isDateDisabled = disableWeekends;
 * "}
 * @event change Change event will fire when either date is updated
 * @status Production
 * @theme flo
 */
class KatalDaterangepicker extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {string | Date} start-value Set the start date value for the date pickers
       */
      startValue: {
        attribute: 'start-value',
        reflect: true,
        converter: dateToStringConverter,
      },

      /**
       * @classprop {string | Date} end-value Set the end date value for the date pickers
       */
      endValue: {
        attribute: 'end-value',
        reflect: true,
        converter: dateToStringConverter,
      },

      /**
       * @classprop {string} start-name The name of the start field, used when
       * sending form data to a server.
       */
      startName: { type: String, attribute: 'start-name', reflect: true },

      /**
       * @classprop {string} end-name The name of the end field, used when
       * sending form data to a server.
       */
      endName: { type: String, attribute: 'end-name', reflect: true },

      /**
       * @param {string} locale Set the locale for the date pickers
       * @required
       */
      locale: { type: String, reflect: true },

      /**
       * @param {string} end-label Set the label for the end date pickers
       * @required
       */
      endLabel: { type: String, attribute: 'end-label', reflect: true },

      /**
       * @param {string} start-label Set the label for the start date pickers
       * @required
       */
      startLabel: { type: String, attribute: 'start-label', reflect: true },

      /**
       * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
       * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
       * more details.
       */
      katal12SpacingFixes: {
        type: Boolean,
        attribute: 'katal-1-2-spacing-fixes',
        converter: booleanConverter,
        reflect: true,
      },
    };
  }

  constructor() {
    super();
    this.locale = 'en-US';
  }

  createRenderRoot() {
    return this;
  }

  _startDateChange(e) {
    e.stopImmediatePropagation();
    this.startValue = e.detail.value;
    this.dispatchCustomEvent('change', { ...this._getRangeState() });
  }

  _endDateChange(e) {
    e.stopImmediatePropagation();
    this.endValue = e.detail.value;
    this.dispatchCustomEvent('change', { ...this._getRangeState() });
  }

  _getRangeState() {
    const start = this.startDatePicker.dateValue;
    const end = this.endDatePicker.dateValue;
    return {
      startValue: this.startValue,
      endValue: this.endValue,
      startDate: start ? start.toISOString() : '',
      endDate: end ? end.toISOString() : '',
    };
  }

  firstUpdated() {
    const datePickers = this.querySelectorAll('kat-date-picker');
    this.startDatePicker = datePickers[0];
    this.endDatePicker = datePickers[1];
  }

  updated(changedProperties) {
    if (changedProperties.has('startValue')) {
      this.startDatePicker.value = this.startValue;
    }
    if (changedProperties.has('endValue')) {
      this.endDatePicker.value = this.endValue;
    }
  }

  /**
   * @classprop {HTMLElement} start-picker The first date picker element that represents the start date.
   * @readonly
   */
  get startPicker() {
    return this.startDatePicker;
  }

  /**
   * @classprop {HTMLElement} end-picker The second date picker element that represents the end date.
   * @readonly
   */
  get endPicker() {
    return this.endDatePicker;
  }

  render() {
    return html`
      <kat-date-picker
        name=${ifNotNull(this.startName)}
        locale=${ifNotNull(this.locale)}
        label=${ifNotNull(this.startLabel)}
        ?katal-1-2-spacing-fixes=${this.katal12SpacingFixes}
        @change=${this._startDateChange}
      >
      </kat-date-picker
      ><kat-date-picker
        name=${ifNotNull(this.endName)}
        locale=${ifNotNull(this.locale)}
        label=${ifNotNull(this.endLabel)}
        ?katal-1-2-spacing-fixes=${this.katal12SpacingFixes}
        @change=${this._endDateChange}
      >
      </kat-date-picker>
    `;
  }
}
coerceBooleanClassProperties(KatalDaterangepicker);
KatalComponent.register('kat-date-range-picker', KatalDaterangepicker);
